import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Square, Wrench, AlertCircle, Calendar, Plus, X, MoreVertical,
  MessageSquare, User, Clock, Check, CheckCircle, Search, MapPin, Edit2,
  LayoutGrid, List, Filter, ChevronDown, Ruler, Monitor, Image as ImageIcon,
  Download, Play, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { PanelImage } from '../lib/panelImages';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUTS = {
  'En service':      { bg: 'bg-green-500',  bgLight: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-500',  icon: Check,       label: 'EN SERVICE' },
  'En panne':        { bg: 'bg-red-500',    bgLight: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-500',    icon: AlertCircle, label: 'EN PANNE' },
  'En réparation':   { bg: 'bg-orange-500', bgLight: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-500', icon: Wrench,      label: 'EN RÉPARATION' },
  'Entretien':       { bg: 'bg-blue-500',   bgLight: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-500',   icon: Wrench,      label: 'ENTRETIEN' },
  'A venir':         { bg: 'bg-sky-500',    bgLight: 'bg-sky-50',    text: 'text-sky-700',    border: 'border-sky-500',    icon: Calendar,    label: 'À VENIR' },
  'Hors service':    { bg: 'bg-gray-500',   bgLight: 'bg-gray-100',  text: 'text-gray-700',   border: 'border-gray-500',   icon: X,           label: 'HORS SERVICE' }
};

const getStatutConfig = (statut) => STATUTS[statut] || STATUTS['En service'];
const STATUT_OPTIONS = Object.keys(STATUTS);

const MOTIFS_RDV = [
  { value: 'Nettoyage',                 color: 'bg-cyan-500',    icon: '🧽' },
  { value: 'Entretien',                 color: 'bg-teal-500',    icon: '⚙️' },
  { value: 'Maintenance',               color: 'bg-blue-500',    icon: '🔧' },
  { value: 'Changement de toile',       color: 'bg-purple-500',  icon: '🖼️' },
  { value: 'Changement des affiches',   color: 'bg-fuchsia-500', icon: '📋' },
  { value: 'Changement d\'emplacement', color: 'bg-amber-500',   icon: '📍' },
  { value: 'Visite',                    color: 'bg-sky-500',     icon: '👁️' },
  { value: 'Inspection',                color: 'bg-indigo-500',  icon: '🔍' },
  { value: 'Contrôle qualité',          color: 'bg-emerald-500', icon: '✅' },
  { value: 'Réparation',                color: 'bg-orange-500',  icon: '🛠️' },
  { value: 'Diagnostic',                color: 'bg-yellow-500',  icon: '🩺' },
  { value: 'Remplacement pièce',        color: 'bg-pink-500',    icon: '🔩' },
  { value: 'Installation',              color: 'bg-green-500',   icon: '📦' },
  { value: 'Désinstallation',           color: 'bg-rose-500',    icon: '📤' },
  { value: 'Mise en service',           color: 'bg-lime-500',    icon: '⚡' },
  { value: 'Arrêt / Extinction',        color: 'bg-slate-500',   icon: '⛔' },
  { value: 'Éclairage / Électricité',   color: 'bg-yellow-600',  icon: '💡' },
  { value: 'Peinture / Rénovation',     color: 'bg-red-500',     icon: '🎨' },
  { value: 'Vérification commerciale',  color: 'bg-violet-500',  icon: '💼' },
  { value: 'Photo / Reportage',         color: 'bg-stone-500',   icon: '📷' },
  { value: 'Autre',                     color: 'bg-gray-500',    icon: '📌' }
];

const getMotifConfig = (motif) => MOTIFS_RDV.find(m => m.value === motif) || MOTIFS_RDV[MOTIFS_RDV.length - 1];

// Emplacements suggérés par type de panneau (alignés sur le module Emplacement)
const EMPLACEMENTS_DEROULANTS = [
  'Devant Moodbucks (RN6)',
  'Devant l\'église Cathédrale',
  'Avenue Touadera',
  'Devant l\'assemblée nationale (Av de l\'indépendance)',
  'Route du lycée français CDG',
  'Devant pharmacie km5 (km5)',
  'Route hôpital communautaire (av des martyrs)',
  'Devant MINUSCA (Av B Boganda)',
  'Devant CC Bangui mall (av de l\'indépendance)',
  'Devant l\'hôtel Oubangui (bd du général de gaule)'
];
const EMPLACEMENTS_INTERIEURS = [
  'Aéroport Bangui Mpoko', 'Hotel LEDGER PLAZZA', 'Centre commercial Bangui Mall'
];
const EMPLACEMENTS_GRAND_FORMAT = [
  'Centre ville Rond point 0'
];

const getEmplacementsForType = (type) => {
  if (type === 'Intérieur LED') return EMPLACEMENTS_INTERIEURS;
  if (type === 'Grand Format') return EMPLACEMENTS_GRAND_FORMAT;
  return EMPLACEMENTS_DEROULANTS;
};

export default function NosPanneaux({ user, onLogout }) {
  const [panneaux, setPanneaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatut, setFilterStatut] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPanneau, setSelectedPanneau] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRdvModal, setShowRdvModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showAffichesModal, setShowAffichesModal] = useState(false);
  const [newRdv, setNewRdv] = useState({ date: '', heure: '', technicien: '', motif: 'Nettoyage', description: '' });
  const [newComment, setNewComment] = useState('');

  useEffect(() => { loadPanneaux(); }, []);

  const loadPanneaux = async () => {
    try {
      const { data } = await axios.get(`${API}/api/nos-panneaux`);
      setPanneaux(data);
    } catch (e) {
      toast.error('Erreur de chargement des panneaux');
    } finally {
      setLoading(false);
    }
  };

  const updatePanneau = async (id, changes) => {
    try {
      await axios.put(`${API}/api/nos-panneaux/${id}`, changes);
      setPanneaux(prev => prev.map(p => p.id === id ? { ...p, ...changes } : p));
      if (selectedPanneau?.id === id) setSelectedPanneau(prev => ({ ...prev, ...changes }));
      toast.success('Panneau mis à jour');
    } catch (e) {
      toast.error('Erreur de mise à jour');
    }
  };

  const handleChangeStatut = (panneau, newStatut) => {
    updatePanneau(panneau.id, { statut: newStatut });
  };

  const handleScheduleRdv = async () => {
    if (!selectedPanneau || !newRdv.date) {
      toast.error('Sélectionnez au moins une date');
      return;
    }
    // Le statut reste inchangé (vert "En service" par défaut) même si un RDV est pris
    await updatePanneau(selectedPanneau.id, {
      prochain_rdv: newRdv.date,
      heure_rdv: newRdv.heure,
      technicien: newRdv.technicien,
      motif_rdv: newRdv.motif,
      description_probleme: newRdv.description
    });
    setShowRdvModal(false);
    setNewRdv({ date: '', heure: '', technicien: '', motif: 'Nettoyage', description: '' });
  };

  const handleAddComment = async () => {
    if (!selectedPanneau || !newComment.trim()) return;
    try {
      await axios.post(`${API}/api/nos-panneaux/${selectedPanneau.id}/commentaire`, { texte: newComment });
      toast.success('Commentaire ajouté');
      setNewComment('');
      setShowCommentModal(false);
      loadPanneaux();
    } catch (e) {
      toast.error('Erreur ajout commentaire');
    }
  };

  const stats = {
    'En service':    panneaux.filter(p => p.statut === 'En service').length,
    'En panne':      panneaux.filter(p => p.statut === 'En panne').length,
    'En réparation': panneaux.filter(p => p.statut === 'En réparation').length,
    'A venir':       panneaux.filter(p => p.statut === 'A venir').length
  };

  // Sort & view state (nouvelle interface)
  const [sortBy, setSortBy] = useState('nom-asc');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Stats par catégorie (ligne)
  const statsForLine = (ligne) => {
    const list = panneaux.filter(p => p.ligne === ligne);
    return {
      total: list.length,
      enService: list.filter(p => p.statut === 'En service').length,
      enPanne: list.filter(p => p.statut === 'En panne').length,
      enReparation: list.filter(p => p.statut === 'En réparation').length,
    };
  };
  const lineLabels = {
    1: 'Panneaux déroulants',
    2: 'Panneaux LED',
    3: 'Panneaux grand format',
    4: 'Panneaux poteaux'
  };
  const lineAccent = {
    1: { text: 'text-blue-600', border: 'border-blue-500', ring: 'ring-blue-200', bar: 'bg-blue-500' },
    2: { text: 'text-emerald-600', border: 'border-emerald-500', ring: 'ring-emerald-200', bar: 'bg-emerald-500' },
    3: { text: 'text-violet-600', border: 'border-violet-500', ring: 'ring-violet-200', bar: 'bg-violet-500' },
    4: { text: 'text-cyan-600', border: 'border-cyan-500', ring: 'ring-cyan-200', bar: 'bg-cyan-500' }
  };
  const s1 = statsForLine(1), s2 = statsForLine(2), s3 = statsForLine(3), s4 = statsForLine(4);

  const sortList = (list) => {
    const arr = [...list];
    if (sortBy === 'nom-asc')    arr.sort((a, b) => (a.numero || '').localeCompare(b.numero || ''));
    if (sortBy === 'nom-desc')   arr.sort((a, b) => (b.numero || '').localeCompare(a.numero || ''));
    if (sortBy === 'statut')     arr.sort((a, b) => (a.statut || '').localeCompare(b.statut || ''));
    if (sortBy === 'emplacement') arr.sort((a, b) => (a.emplacement || '').localeCompare(b.emplacement || ''));
    return arr;
  };

  const filterPanneaux = (list) => {
    return list.filter(p => {
      if (filterStatut !== 'all' && p.statut !== filterStatut) return false;
      if (searchQuery && !`${p.numero} ${p.emplacement} ${p.type}`.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    });
  };

  const deroulants  = sortList(filterPanneaux(panneaux.filter(p => p.ligne === 1)));
  const interieurs  = sortList(filterPanneaux(panneaux.filter(p => p.ligne === 2)));
  const grandFormat = sortList(filterPanneaux(panneaux.filter(p => p.ligne === 3)));
  const poteaux     = sortList(filterPanneaux(panneaux.filter(p => p.ligne === 4)));

  // Emplacements récents (dérivés du dataset — on regroupe par emplacement, tri desc par date_installation)
  const emplacementsRecents = (() => {
    const map = new Map();
    panneaux.forEach(p => {
      if (!p.emplacement) return;
      const key = p.emplacement;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, { emplacement: key, ville: p.ville || '', count: 1, date: p.date_installation || '' });
      } else {
        existing.count += 1;
        if ((p.date_installation || '') > existing.date) existing.date = p.date_installation;
      }
    });
    return [...map.values()].sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 4);
  })();

  const totalPanneaux = panneaux.length || 1;
  const pctEnService    = Math.round((stats['En service']    / totalPanneaux) * 100);
  const pctEnPanne      = Math.round((stats['En panne']      / totalPanneaux) * 100);
  const pctEnReparation = Math.round((stats['En réparation'] / totalPanneaux) * 100);

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="panneaux">
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="panneaux">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* ============ COLONNE PRINCIPALE ============ */}
        <div className="space-y-5">
          {/* Header */}
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Nos Panneaux</h1>
              <p className="text-gray-500 text-sm mt-1">Gestion et suivi de vos panneaux publicitaires</p>
            </div>
            <div className="flex gap-2">
              <Popover open={showFilterPanel} onOpenChange={setShowFilterPanel}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2 h-9" data-testid="btn-filter-panneaux">
                    <Filter className="h-4 w-4" /> Filtrer
                    {filterStatut !== 'all' && <span className="w-2 h-2 rounded-full bg-blue-500"></span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-3">
                  <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Filtrer par statut</p>
                  <div className="space-y-1">
                    <button onClick={() => { setFilterStatut('all'); setShowFilterPanel(false); }}
                      className={`w-full text-left px-3 py-2 rounded text-sm ${filterStatut === 'all' ? 'bg-blue-50 text-blue-700 font-semibold' : 'hover:bg-gray-50'}`}>
                      Tous les panneaux ({panneaux.length})
                    </button>
                    {['En service', 'En panne', 'En réparation', 'A venir'].map(st => {
                      const cfg = getStatutConfig(st);
                      return (
                        <button key={st}
                          onClick={() => { setFilterStatut(st); setShowFilterPanel(false); }}
                          className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${filterStatut === st ? 'bg-blue-50 text-blue-700 font-semibold' : 'hover:bg-gray-50'}`}>
                          <span className={`w-2 h-2 rounded-full ${cfg.bg}`}></span>
                          {st === 'A venir' ? 'À venir' : st} ({stats[st] ?? 0})
                        </button>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
              <Button size="sm" className="gap-2 h-9 bg-blue-600 hover:bg-blue-700" data-testid="btn-add-panneau"
                onClick={() => toast.info("La création manuelle de panneaux se fait via le module Emplacements.")}>
                <Plus className="h-4 w-4" /> Ajouter un panneau
              </Button>
            </div>
          </div>

          {/* 4 Category Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <CategoryCard
              label="Panneaux déroulants"
              stats={s1}
              accent={lineAccent[1]}
              active={filterStatut === 'all'}
              icon="deroulant"
              onClick={() => { setFilterStatut('all'); }}
            />
            <CategoryCard
              label="Panneaux LED"
              stats={s2}
              accent={lineAccent[2]}
              active={false}
              icon="led"
            />
            <CategoryCard
              label="Panneaux grand format"
              stats={s3}
              accent={lineAccent[3]}
              active={false}
              icon="grand"
            />
            <CategoryCard
              label="Panneaux poteaux"
              stats={s4}
              accent={lineAccent[4]}
              active={false}
              icon="poteau"
            />
          </div>

          {/* Search + Sort + View */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px] relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Rechercher un panneau, emplacement, type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-10 bg-white border-gray-200"
                data-testid="panneaux-search"
              />
            </div>
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid="view-grid"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid="view-list"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[190px] h-10 bg-white" data-testid="sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nom-asc">Trier par : Nom (A-Z)</SelectItem>
                <SelectItem value="nom-desc">Trier par : Nom (Z-A)</SelectItem>
                <SelectItem value="statut">Trier par : Statut</SelectItem>
                <SelectItem value="emplacement">Trier par : Emplacement</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Groupes de panneaux */}
          <PanneauGroup title="Panneaux déroulants" count={deroulants.length} accent={lineAccent[1]}>
            <PanneauGrid
              panneaux={deroulants}
              viewMode={viewMode}
              onOpenDetail={(p) => { setSelectedPanneau(p); setShowDetailModal(true); }}
              onChangeStatut={(p, s) => handleChangeStatut(p, s)}
              onScheduleRdv={(p) => { setSelectedPanneau(p); setShowRdvModal(true); }}
              onAddComment={(p) => { setSelectedPanneau(p); setShowCommentModal(true); }}
            />
          </PanneauGroup>

          <PanneauGroup title="Panneaux LED" count={interieurs.length} accent={lineAccent[2]}>
            <PanneauGrid
              panneaux={interieurs}
              viewMode={viewMode}
              onOpenDetail={(p) => { setSelectedPanneau(p); setShowDetailModal(true); }}
              onChangeStatut={(p, s) => handleChangeStatut(p, s)}
              onScheduleRdv={(p) => { setSelectedPanneau(p); setShowRdvModal(true); }}
              onAddComment={(p) => { setSelectedPanneau(p); setShowCommentModal(true); }}
            />
          </PanneauGroup>

          <PanneauGroup title="Panneaux grand format" count={grandFormat.length} accent={lineAccent[3]}>
            <PanneauGrid
              panneaux={grandFormat}
              viewMode={viewMode}
              onOpenDetail={(p) => { setSelectedPanneau(p); setShowDetailModal(true); }}
              onChangeStatut={(p, s) => handleChangeStatut(p, s)}
              onScheduleRdv={(p) => { setSelectedPanneau(p); setShowRdvModal(true); }}
              onAddComment={(p) => { setSelectedPanneau(p); setShowCommentModal(true); }}
            />
          </PanneauGroup>

          <PanneauGroup title="Panneaux poteaux" count={poteaux.length} accent={lineAccent[4]}>
            <PanneauGrid
              panneaux={poteaux}
              viewMode={viewMode}
              onOpenDetail={(p) => { setSelectedPanneau(p); setShowDetailModal(true); }}
              onChangeStatut={(p, s) => handleChangeStatut(p, s)}
              onScheduleRdv={(p) => { setSelectedPanneau(p); setShowRdvModal(true); }}
              onAddComment={(p) => { setSelectedPanneau(p); setShowCommentModal(true); }}
            />
          </PanneauGroup>
        </div>

        {/* ============ SIDEBAR DROITE ============ */}
        <aside className="space-y-4">
          {/* État des panneaux — donut */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <h3 className="text-base font-bold text-gray-900 mb-4">État des panneaux</h3>
            <div className="flex items-center justify-center relative">
              <Donut
                total={panneaux.length}
                segments={[
                  { value: stats['En service'],    color: '#10b981' },
                  { value: stats['En réparation'], color: '#f59e0b' },
                  { value: stats['En panne'],      color: '#ef4444' },
                ]}
              />
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <LegendRow color="#10b981" label="En service" count={stats['En service']} pct={pctEnService} />
              <LegendRow color="#ef4444" label="En panne"   count={stats['En panne']}   pct={pctEnPanne} />
              <LegendRow color="#f59e0b" label="En réparation" count={stats['En réparation']} pct={pctEnReparation} />
            </div>
          </div>

          {/* Répartition par type */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <h3 className="text-base font-bold text-gray-900 mb-4">Répartition par type</h3>
            <div className="space-y-3">
              <TypeBar label="Panneaux déroulants" count={s1.total} total={panneaux.length} color="bg-blue-500" />
              <TypeBar label="Panneaux LED"        count={s2.total} total={panneaux.length} color="bg-emerald-500" />
              <TypeBar label="Panneaux grand format" count={s3.total} total={panneaux.length} color="bg-violet-500" />
              <TypeBar label="Panneaux poteaux"    count={s4.total} total={panneaux.length} color="bg-cyan-500" />
            </div>
          </div>

          {/* Emplacements récents */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <h3 className="text-base font-bold text-gray-900 mb-4">Emplacements récents</h3>
            <div className="space-y-3">
              {emplacementsRecents.map((e, i) => (
                <div key={i} className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0">
                    <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{e.emplacement}</div>
                      {e.ville && <div className="text-xs text-gray-500">{e.ville}</div>}
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">{e.count} panneau{e.count > 1 ? 'x' : ''}</span>
                </div>
              ))}
              {emplacementsRecents.length === 0 && (
                <p className="text-xs text-gray-400 italic">Aucun emplacement</p>
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* Modals */}
      <DetailModal
        open={showDetailModal}
        onOpenChange={setShowDetailModal}
        panneau={selectedPanneau}
        onUpdate={(changes) => selectedPanneau && updatePanneau(selectedPanneau.id, changes)}
        onScheduleRdv={() => { setShowDetailModal(false); setShowRdvModal(true); }}
        onAddComment={() => { setShowDetailModal(false); setShowCommentModal(true); }}
        onOpenAffiches={() => setShowAffichesModal(true)}
      />

      <AffichesModal
        open={showAffichesModal}
        onOpenChange={setShowAffichesModal}
        panneau={selectedPanneau}
      />

      <RdvModal
        open={showRdvModal}
        onOpenChange={setShowRdvModal}
        panneau={selectedPanneau}
        newRdv={newRdv}
        setNewRdv={setNewRdv}
        onSubmit={handleScheduleRdv}
      />

      <CommentModal
        open={showCommentModal}
        onOpenChange={setShowCommentModal}
        panneau={selectedPanneau}
        comment={newComment}
        setComment={setNewComment}
        onSubmit={handleAddComment}
      />
    </Layout>
  );
}

// ============ SUB-COMPONENTS ============

function CategoryCard({ label, stats, accent, icon, active, onClick }) {
  const clickable = !!onClick;
  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl border p-5 shadow-sm transition-all ${clickable ? 'cursor-pointer hover:shadow-md' : ''} ${active ? `${accent.border} ring-4 ${accent.ring}` : 'border-gray-200'}`}
      data-testid={`cat-card-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={`text-sm font-semibold ${accent.text}`}>{label}</p>
          <p className="text-4xl font-bold text-gray-900 mt-2 leading-none">{stats.total}</p>
        </div>
        <CategoryIcon type={icon} accent={accent} />
      </div>
      <div className="mt-4 flex items-center gap-4 flex-wrap text-xs">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
          <span className="text-gray-600">En service {stats.enService}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-red-500"></span>
          <span className="text-gray-600">En panne {stats.enPanne}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-amber-500"></span>
          <span className="text-gray-600">En réparation {stats.enReparation}</span>
        </div>
      </div>
    </div>
  );
}

function CategoryIcon({ type }) {
  return (
    <div className="w-16 h-16 rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0" data-testid={`cat-icon-${type}`}>
      <PanelImage type={type} className="h-full w-full p-0.5" />
    </div>
  );
}

function PanneauGroup({ title, count, accent, children }) {
  if (count === 0) return null;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold text-gray-900">{title} <span className="text-gray-400 font-normal">({count})</span></h2>
        <button className={`text-sm ${accent.text} hover:underline`}>Voir tout ({count}) ›</button>
      </div>
      {children}
    </div>
  );
}

function PanneauGrid({ panneaux, viewMode, onOpenDetail, onChangeStatut, onScheduleRdv, onAddComment }) {
  if (panneaux.length === 0) {
    return <p className="text-sm text-gray-400 italic px-1">Aucun panneau correspondant.</p>;
  }
  if (viewMode === 'list') {
    return (
      <div className="bg-white rounded-lg border border-gray-200 divide-y">
        {panneaux.map(p => (
          <PanneauListRow
            key={p.id}
            panneau={p}
            onOpenDetail={() => onOpenDetail(p)}
            onChangeStatut={(s) => onChangeStatut(p, s)}
            onScheduleRdv={() => onScheduleRdv(p)}
            onAddComment={() => onAddComment(p)}
          />
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
      {panneaux.map(p => (
        <PanneauCard
          key={p.id}
          panneau={p}
          onOpenDetail={() => onOpenDetail(p)}
          onChangeStatut={(s) => onChangeStatut(p, s)}
          onScheduleRdv={() => onScheduleRdv(p)}
          onAddComment={() => onAddComment(p)}
        />
      ))}
    </div>
  );
}

function StatutBadge({ statut }) {
  const cfg = getStatutConfig(statut);
  const map = {
    'En service':    { bg: 'bg-emerald-50', text: 'text-emerald-600', dot: 'bg-emerald-500' },
    'En panne':      { bg: 'bg-red-50',     text: 'text-red-600',     dot: 'bg-red-500' },
    'En réparation': { bg: 'bg-amber-50',   text: 'text-amber-600',   dot: 'bg-amber-500' },
    'Entretien':     { bg: 'bg-blue-50',    text: 'text-blue-600',    dot: 'bg-blue-500' },
    'A venir':       { bg: 'bg-sky-50',     text: 'text-sky-600',     dot: 'bg-sky-500' },
    'Hors service':  { bg: 'bg-gray-100',   text: 'text-gray-600',    dot: 'bg-gray-500' }
  }[statut] || { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-500' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${map.bg} ${map.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${map.dot}`}></span>
      {statut === 'A venir' ? 'À venir' : statut}
    </span>
  );
}

function PanneauCard({ panneau, onOpenDetail, onChangeStatut, onScheduleRdv, onAddComment }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [statutMenuOpen, setStatutMenuOpen] = useState(false);
  const iconType = panneau.ligne === 2 ? 'led' : panneau.ligne === 3 ? 'grand' : panneau.ligne === 4 ? 'poteau' : 'deroulant';
  const accent = panneau.ligne === 2 ? { text: 'text-emerald-500' } : panneau.ligne === 3 ? { text: 'text-violet-500' } : panneau.ligne === 4 ? { text: 'text-cyan-500' } : { text: 'text-blue-500' };

  return (
    <Card
      className="bg-white border border-gray-200 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
      onClick={onOpenDetail}
      data-testid={`panneau-card-${panneau.numero}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <span className="text-sm font-semibold text-gray-400">{panneau.numero}</span>
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <StatutBadge statut={panneau.statut} />
            <Popover open={menuOpen} onOpenChange={setMenuOpen}>
              <PopoverTrigger asChild>
                <button
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(true); }}
                  className="p-1 rounded hover:bg-gray-100"
                  data-testid={`panneau-menu-${panneau.numero}`}
                >
                  <MoreVertical className="h-4 w-4 text-gray-400" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-60 p-0" onClick={(e) => e.stopPropagation()}>
                <div className="py-1">
                  <Popover open={statutMenuOpen} onOpenChange={setStatutMenuOpen}>
                    <PopoverTrigger asChild>
                      <button className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2">
                        <Edit2 className="h-4 w-4 text-blue-600" /> Changer le statut
                      </button>
                    </PopoverTrigger>
                    <PopoverContent side="right" className="w-48 p-0">
                      {STATUT_OPTIONS.map(st => {
                        const c = getStatutConfig(st);
                        return (
                          <button
                            key={st}
                            onClick={() => { onChangeStatut(st); setStatutMenuOpen(false); setMenuOpen(false); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                          >
                            <div className={`w-2.5 h-2.5 rounded-full ${c.bg}`}></div>
                            {st}
                          </button>
                        );
                      })}
                    </PopoverContent>
                  </Popover>
                  <button
                    onClick={() => { setMenuOpen(false); onScheduleRdv(); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                    data-testid={`menu-schedule-${panneau.numero}`}
                  >
                    <Calendar className="h-4 w-4 text-purple-600" /> Programmer intervention / RDV
                  </button>
                  <button
                    onClick={() => { setMenuOpen(false); onAddComment(); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                    data-testid={`menu-comment-${panneau.numero}`}
                  >
                    <MessageSquare className="h-4 w-4 text-green-600" /> Ajouter un commentaire
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 ${accent.text}`}>
            <CategoryIcon type={iconType} accent={accent} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              Panneau {panneau.ligne === 2 ? 'LED' : panneau.ligne === 3 ? 'grand format' : panneau.ligne === 4 ? 'poteau' : 'déroulant'} {panneau.numero}
            </h3>
          </div>
        </div>

        <div className="mt-3 space-y-1.5 text-xs text-gray-600">
          <div className="flex items-start gap-1.5">
            <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
            <span className="line-clamp-2 leading-snug">{panneau.emplacement || '—'}</span>
          </div>
          {panneau.ville && <div className="pl-5 text-gray-500">{panneau.ville}</div>}
          {panneau.reference && (
            <div className="flex items-center gap-1.5">
              <Ruler className="h-3.5 w-3.5 text-gray-400" />
              <span>{panneau.reference}</span>
            </div>
          )}
          {panneau.date_installation && (
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 text-gray-400" />
              <span>Installé le {panneau.date_installation}</span>
            </div>
          )}
        </div>

        {/* Occupation badge (light) */}
        {panneau.occupation && (
          <OccupationBadgeLight used={panneau.occupation.used} total={panneau.occupation.total} />
        )}

        {(panneau.motif_rdv || panneau.technicien || panneau.prochain_rdv) && (
          <div className="mt-3 pt-2 border-t border-gray-100 space-y-1 text-xs">
            {panneau.motif_rdv && (
              <div className="flex items-center gap-1.5 text-gray-700">
                <span>{getMotifConfig(panneau.motif_rdv).icon}</span>
                <span className="font-medium truncate">{panneau.motif_rdv}</span>
              </div>
            )}
            {panneau.technicien && (
              <div className="flex items-center gap-1.5 text-gray-500">
                <User className="h-3 w-3" />
                <span className="truncate">{panneau.technicien}</span>
              </div>
            )}
            {panneau.prochain_rdv && (
              <div className="flex items-center gap-1.5 text-gray-500">
                <Clock className="h-3 w-3" />
                <span>{panneau.prochain_rdv}{panneau.heure_rdv ? ` à ${panneau.heure_rdv}` : ''}</span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PanneauListRow({ panneau, onOpenDetail, onScheduleRdv, onAddComment }) {
  return (
    <div
      onClick={onOpenDetail}
      className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
      data-testid={`panneau-row-${panneau.numero}`}
    >
      <span className="text-xs font-semibold text-gray-400 w-8">{panneau.numero}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-gray-900 truncate">
            Panneau {panneau.ligne === 2 ? 'LED' : panneau.ligne === 3 ? 'grand format' : panneau.ligne === 4 ? 'poteau' : 'déroulant'} {panneau.numero}
          </h4>
          <StatutBadge statut={panneau.statut} />
        </div>
        <div className="text-xs text-gray-500 truncate">
          <MapPin className="inline h-3 w-3 mr-1" />
          {panneau.emplacement} {panneau.ville ? `— ${panneau.ville}` : ''}
        </div>
      </div>
      <div className="hidden md:block text-xs text-gray-500 whitespace-nowrap">
        {panneau.reference || '—'}
      </div>
      <div className="hidden lg:block text-xs text-gray-500 whitespace-nowrap">
        {panneau.date_installation || '—'}
      </div>
    </div>
  );
}

function OccupationBadgeLight({ used, total }) {
  const u = Math.max(0, Number(used) || 0);
  const t = Math.max(1, Number(total) || 1);
  const pct = Math.min(100, Math.round((u / t) * 100));
  let color = 'bg-emerald-500';
  if (u >= t) color = 'bg-red-500';
  else if (pct >= 90) color = 'bg-red-500';
  else if (pct >= 50) color = 'bg-amber-500';
  return (
    <div className="mt-3 flex items-center gap-2" data-testid="panneau-occupation" title={`${u}/${t} affiches en cours`}>
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }}></div>
      </div>
      <span className="text-[11px] font-semibold text-gray-500 tabular-nums">{u}/{t}</span>
    </div>
  );
}

function Donut({ total, segments }) {
  const R = 46, C = 2 * Math.PI * R;
  const sum = segments.reduce((s, seg) => s + seg.value, 0) || 1;
  let offset = 0;
  return (
    <div className="relative w-40 h-40">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r={R} fill="none" stroke="#f3f4f6" strokeWidth="14" />
        {segments.map((seg, i) => {
          const len = (seg.value / sum) * C;
          const el = (
            <circle
              key={i} cx="60" cy="60" r={R} fill="none"
              stroke={seg.color} strokeWidth="14"
              strokeDasharray={`${len} ${C - len}`}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
            />
          );
          offset += len;
          return el;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-3xl font-bold text-gray-900">{total}</div>
        <div className="text-xs text-gray-500">Total</div>
      </div>
    </div>
  );
}

function LegendRow({ color, label, count, pct }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }}></span>
        <span className="text-gray-700">{label} ({count})</span>
      </div>
      <span className="text-gray-500 font-medium">{pct || 0}%</span>
    </div>
  );
}

function TypeBar({ label, count, total, color }) {
  const pct = Math.round((count / (total || 1)) * 100);
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-gray-700">{label}</span>
        <span className="text-gray-500 font-medium">{count} ({pct}%)</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }}></div>
      </div>
    </div>
  );
}

function DetailModal({ open, onOpenChange, panneau, onUpdate, onScheduleRdv, onAddComment, onOpenAffiches }) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [showRenameEmp, setShowRenameEmp] = useState(false);

  useEffect(() => {
    if (panneau) setDraft({ ...panneau });
    setIsEditing(false);
  }, [panneau]);

  if (!panneau || !draft) return null;
  const cfg = getStatutConfig(panneau.statut);

  const save = () => {
    onUpdate({
      ville: draft.ville,
      reference: draft.reference,
      puissance: draft.puissance,
      numero_serie: draft.numero_serie,
      date_installation: draft.date_installation,
      notes: draft.notes,
      technicien: draft.technicien,
      description_probleme: draft.description_probleme
    });
    setIsEditing(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className={`w-12 h-12 ${cfg.bg} rounded-lg flex items-center justify-center text-white text-xl font-bold`}>
              {panneau.numero}
            </div>
            <div>
              <div className="text-xl">Panneau {panneau.numero} — {panneau.type}</div>
              <div className="text-sm text-gray-500 font-normal">{panneau.reference} • Ligne {panneau.ligne}</div>
            </div>
          </DialogTitle>
          <DialogDescription>Gérez toutes les informations et interventions du panneau.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Statut rapide */}
          <div className={`p-4 rounded-lg border-2 ${cfg.border} ${cfg.bgLight}`}>
            <Label className="text-xs font-bold uppercase text-gray-600">Statut actuel</Label>
            <Select value={panneau.statut} onValueChange={(v) => onUpdate({ statut: v })}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUT_OPTIONS.map(st => {
                  const c = getStatutConfig(st);
                  return (
                    <SelectItem key={st} value={st}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${c.bg}`}></div>
                        {st}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Emplacement - modification rapide */}
          <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-blue-600" />
                <Label className="text-sm font-bold uppercase text-blue-700">Emplacement du panneau</Label>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  className="gap-1 bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={() => { onOpenChange(false); onOpenAffiches && onOpenAffiches(); }}
                  data-testid="btn-nos-affiches"
                >
                  <ImageIcon className="h-3 w-3" />
                  Nos affiches
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1 border-blue-300 text-blue-700 hover:bg-blue-100"
                  onClick={() => setShowRenameEmp(true)}
                  data-testid="btn-open-rename-emp"
                >
                  <Edit2 className="h-3 w-3" />
                  Renommer
                </Button>
              </div>
            </div>
            <Select
              value={panneau.emplacement}
              onValueChange={(v) => onUpdate({ emplacement: v })}
            >
              <SelectTrigger className="bg-white" data-testid="emplacement-select">
                <SelectValue placeholder="Sélectionner un emplacement" />
              </SelectTrigger>
              <SelectContent>
                {getEmplacementsForType(panneau.type).map(loc => (
                  <SelectItem key={loc} value={loc}>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3 w-3 text-blue-500" />
                      {loc}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-blue-600 mt-2">Emplacements suggérés pour le type <strong>{panneau.type}</strong>. Le changement est enregistré instantanément.</p>
            <div className="mt-3 flex items-center gap-2">
              <Input
                placeholder="Ou saisir un nouvel emplacement personnalisé..."
                value={draft.emplacement !== panneau.emplacement ? draft.emplacement : ''}
                onChange={(e) => setDraft({ ...draft, emplacement: e.target.value })}
                className="flex-1 text-sm"
                data-testid="emplacement-custom-input"
              />
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  if (draft.emplacement && draft.emplacement !== panneau.emplacement) {
                    onUpdate({ emplacement: draft.emplacement });
                  }
                }}
              >
                Appliquer
              </Button>
            </div>
          </div>

          {/* Modal rename emplacement (renomme la fiche canonique dans /api/emplacements) */}
          <RenameEmplacementDialog
            open={showRenameEmp}
            onOpenChange={setShowRenameEmp}
            currentName={panneau.emplacement}
            onRenamed={(newName) => onUpdate({ emplacement: newName })}
          />

          {/* Infos */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-sm uppercase text-gray-700">Informations techniques</h3>
              <Button size="sm" variant="outline" onClick={() => setIsEditing(!isEditing)}>
                <Edit2 className="h-3 w-3 mr-1" /> {isEditing ? 'Annuler' : 'Modifier'}
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Emplacement actuel" value={panneau.emplacement} />
              <Field label="Ville" value={draft.ville} editable={isEditing} onChange={(v) => setDraft({ ...draft, ville: v })} />
              <Field label="Référence" value={draft.reference} editable={isEditing} onChange={(v) => setDraft({ ...draft, reference: v })} />
              <Field label="Puissance" value={draft.puissance} editable={isEditing} onChange={(v) => setDraft({ ...draft, puissance: v })} />
              <Field label="N° de série" value={draft.numero_serie} editable={isEditing} onChange={(v) => setDraft({ ...draft, numero_serie: v })} />
              <Field label="Date installation" value={draft.date_installation} editable={isEditing} onChange={(v) => setDraft({ ...draft, date_installation: v })} />
              <Field label="Technicien assigné" value={draft.technicien} editable={isEditing} onChange={(v) => setDraft({ ...draft, technicien: v })} />
              <Field label="Prochain RDV" value={draft.prochain_rdv ? `${draft.prochain_rdv}${draft.heure_rdv ? ` à ${draft.heure_rdv}` : ''}` : '—'} />
            </div>
            {draft.motif_rdv && (
              <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded flex items-center gap-2">
                <span className="text-xl">{getMotifConfig(draft.motif_rdv).icon}</span>
                <div>
                  <p className="text-xs text-purple-600 font-semibold uppercase">Motif du RDV</p>
                  <p className="font-bold text-purple-900">{draft.motif_rdv}</p>
                </div>
              </div>
            )}
            {draft.description_probleme && (
              <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded text-sm">
                <span className="font-semibold text-orange-700">Problème signalé : </span>
                {isEditing ? (
                  <Textarea
                    value={draft.description_probleme}
                    onChange={(e) => setDraft({ ...draft, description_probleme: e.target.value })}
                    rows={2}
                  />
                ) : <span>{draft.description_probleme}</span>}
              </div>
            )}
            {isEditing && (
              <Button onClick={save} size="sm" className="mt-3 bg-blue-600 hover:bg-blue-700 w-full">
                <CheckCircle className="h-4 w-4 mr-2" /> Enregistrer les modifications
              </Button>
            )}
          </div>

          {/* Commentaires */}
          {panneau.commentaires && panneau.commentaires.length > 0 && (
            <div className="border rounded-lg p-4">
              <h3 className="font-bold text-sm uppercase text-gray-700 mb-3">Commentaires ({panneau.commentaires.length})</h3>
              <div className="space-y-2 max-h-52 overflow-y-auto">
                {panneau.commentaires.slice().reverse().map((c) => (
                  <div key={c.id} className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded text-sm">
                    <div className="flex justify-between mb-1">
                      <span className="font-semibold text-blue-900">{c.auteur}</span>
                      <span className="text-xs text-gray-500">{new Date(c.date).toLocaleString('fr-FR')}</span>
                    </div>
                    <p className="text-gray-700">{c.texte}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label className="text-xs font-bold uppercase text-gray-600">Notes internes</Label>
            <Textarea
              value={draft.notes || ''}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              onBlur={() => onUpdate({ notes: draft.notes })}
              placeholder="Notes sur ce panneau..."
              rows={2}
              className="mt-2"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-3 border-t">
            <Button onClick={onScheduleRdv} className="flex-1 bg-purple-600 hover:bg-purple-700 gap-2">
              <Calendar className="h-4 w-4" /> Programmer RDV
            </Button>
            <Button onClick={onAddComment} className="flex-1 bg-green-600 hover:bg-green-700 gap-2">
              <MessageSquare className="h-4 w-4" /> Commentaire
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, editable, onChange }) {
  return (
    <div>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      {editable ? (
        <Input value={value || ''} onChange={(e) => onChange && onChange(e.target.value)} className="h-8" />
      ) : (
        <p className="font-semibold text-gray-900">{value || '—'}</p>
      )}
    </div>
  );
}

function RdvModal({ open, onOpenChange, panneau, newRdv, setNewRdv, onSubmit }) {
  if (!panneau) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Programmer un rendez-vous — Panneau {panneau.numero}</DialogTitle>
          <DialogDescription>{panneau.emplacement} • {panneau.type}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label>Motif du rendez-vous *</Label>
            <Select value={newRdv.motif} onValueChange={(v) => setNewRdv({ ...newRdv, motif: v })}>
              <SelectTrigger data-testid="rdv-motif-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MOTIFS_RDV.map(m => (
                  <SelectItem key={m.value} value={m.value}>
                    <div className="flex items-center gap-2">
                      <span>{m.icon}</span>
                      <span>{m.value}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date *</Label>
              <Input type="date" value={newRdv.date} onChange={(e) => setNewRdv({ ...newRdv, date: e.target.value })} data-testid="rdv-date-input" />
            </div>
            <div>
              <Label>Heure</Label>
              <Input type="time" value={newRdv.heure} onChange={(e) => setNewRdv({ ...newRdv, heure: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Technicien assigné</Label>
            <Input
              placeholder="Ex: Damien Pétro"
              value={newRdv.technicien}
              onChange={(e) => setNewRdv({ ...newRdv, technicien: e.target.value })}
              data-testid="rdv-technicien-input"
            />
          </div>
          <div>
            <Label>Description / Détails supplémentaires</Label>
            <Textarea
              placeholder="Informations complémentaires..."
              value={newRdv.description}
              onChange={(e) => setNewRdv({ ...newRdv, description: e.target.value })}
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button onClick={onSubmit} className="bg-purple-600 hover:bg-purple-700" data-testid="rdv-submit-btn">
              <Calendar className="h-4 w-4 mr-2" /> Programmer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CommentModal({ open, onOpenChange, panneau, comment, setComment, onSubmit }) {
  if (!panneau) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Ajouter un commentaire — Panneau {panneau.numero}</DialogTitle>
          <DialogDescription>{panneau.emplacement}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <Textarea
            placeholder="Votre commentaire..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={5}
            autoFocus
            data-testid="comment-textarea"
          />
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button onClick={onSubmit} className="bg-green-600 hover:bg-green-700" data-testid="comment-submit-btn">
              <MessageSquare className="h-4 w-4 mr-2" /> Publier
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RenameEmplacementDialog({ open, onOpenChange, currentName, onRenamed }) {
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setNewName(currentName || '');
  }, [open, currentName]);

  const save = async () => {
    const target = (newName || '').trim();
    if (!target) { toast.error('Nom vide'); return; }
    if (target === currentName) { onOpenChange(false); return; }
    setSaving(true);
    try {
      // Trouver l'emplacement par nom canonique
      const listRes = await axios.get(`${API}/api/emplacements`);
      const found = (listRes.data || []).find(e => (e.nom || '').trim() === (currentName || '').trim());
      if (!found) {
        toast.error(`Aucun emplacement trouvé nommé "${currentName}"`);
        setSaving(false);
        return;
      }
      // Renommer via l'endpoint canonique
      await axios.put(`${API}/api/emplacements/${found.id}`, { nom: target });
      toast.success('Emplacement renommé');
      onRenamed?.(target);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur renommage');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="rename-emplacement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit2 className="h-5 w-5 text-blue-600" /> Renommer l&apos;emplacement
          </DialogTitle>
          <DialogDescription>
            Ce changement met à jour le nom canonique dans le module Emplacements. Tous les panneaux liés seront automatiquement synchronisés.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-500">Ancien nom</Label>
            <div className="text-sm font-medium text-slate-700 truncate">{currentName || '—'}</div>
          </div>
          <div>
            <Label htmlFor="new-emp-name" className="text-xs text-slate-500">Nouveau nom</Label>
            <Input
              id="new-emp-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Ex : Devant l'hôtel Lévis (Av B.B)"
              data-testid="input-new-emp-name"
              autoFocus
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button
            onClick={save}
            disabled={saving || !newName.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="btn-save-rename-emp"
          >
            {saving ? 'Enregistrement…' : 'Renommer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}



// ═══════════════════════════════════════════════════════════════════
// AffichesModal — affiche les 10 affiches enregistrées pour ce panneau
// ═══════════════════════════════════════════════════════════════════
function AffichesModal({ open, onOpenChange, panneau }) {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);      // { url, name, company, number, isVideo }

  useEffect(() => {
    if (!open || !panneau) return;
    setCampaign(null);
    setNotFound(false);
    setLightbox(null);
    setLoading(true);
    const key = encodeURIComponent(panneau.emplacement_id || panneau.emplacement || panneau.id || '');
    const type = panneau.ligne === 2 ? 'led' : panneau.ligne === 3 ? 'grand' : panneau.ligne === 4 ? 'poteau' : 'roulant';
    axios.get(`${API}/api/affiches/campaign/${key}`, { params: { type, nom: panneau.emplacement || '' } })
      .then(r => setCampaign(r.data))
      .catch(err => {
        if (err?.response?.status === 404) setNotFound(true);
        else toast.error("Erreur chargement des affiches");
      })
      .finally(() => setLoading(false));
  }, [open, panneau]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const fullUrl = (path) => path ? `${backendUrl}${path}` : '';

  const isVideo = campaign?.media_type === 'video';

  const renderSlot = (url, name, company, idx, sideLabel) => {
    const number = idx + 1;
    const hasMedia = Boolean(url);
    return (
      <div key={`${sideLabel}-${idx}`} className="rounded-lg overflow-hidden border-2 border-slate-200 bg-white flex flex-col" data-testid={`af-slot-${sideLabel}-${idx}`}>
        {/* Zone image */}
        <div className="relative aspect-[4/3] bg-slate-50">
          <div className="absolute top-1 left-1 z-10 bg-white/95 text-[10px] font-black text-slate-900 rounded px-1.5 py-0.5 shadow-sm">
            #{number}
          </div>
          {hasMedia && (
            <button
              onClick={(e) => { e.stopPropagation(); setLightbox({ url: fullUrl(url), name, company, number, isVideo }); }}
              className="absolute top-1 right-1 z-10 bg-purple-600 hover:bg-purple-700 text-white rounded p-1 shadow"
              title="Prévisualiser en grand"
              data-testid={`af-preview-${sideLabel}-${idx}`}
            >
              <Eye className="h-3.5 w-3.5" />
            </button>
          )}
          {hasMedia ? (
            isVideo ? (
              <div className="w-full h-full flex flex-col items-center justify-center bg-slate-900 text-white cursor-pointer" onClick={() => setLightbox({ url: fullUrl(url), name, company, number, isVideo })}>
                <Play className="h-8 w-8 mb-1" />
                <div className="text-[10px] font-bold truncate w-full text-center px-2">{name || `Vidéo #${number}`}</div>
              </div>
            ) : (
              <img
                src={fullUrl(url)}
                alt={name || `Affiche ${number}`}
                className="w-full h-full object-cover cursor-pointer"
                onClick={() => setLightbox({ url: fullUrl(url), name, company, number, isVideo })}
              />
            )
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[10px] text-slate-400 italic">Vide</div>
          )}
        </div>
        {/* Bandeau info EN-DESSOUS (jamais sur l'image) */}
        <div className="border-t border-slate-100 p-2 bg-white">
          <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Entreprise</div>
          {company ? (
            <div className="text-xs font-black text-slate-900 truncate" title={company}>{company}</div>
          ) : (
            <div className="text-xs italic text-slate-400">— non renseigné —</div>
          )}
          {name && !isVideo && (
            <div className="text-[10px] text-slate-500 truncate mt-0.5" title={name}>{name}</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="affiches-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-purple-600" />
            Affiches du panneau {panneau?.numero ? `#${panneau.numero}` : ''}
          </DialogTitle>
          {panneau?.emplacement && (
            <DialogDescription className="flex items-center gap-1 text-slate-600">
              <MapPin className="h-3 w-3" /> {panneau.emplacement}
            </DialogDescription>
          )}
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-sm text-slate-500">Chargement des affiches…</div>
        )}

        {notFound && !loading && (
          <div className="py-12 text-center">
            <ImageIcon className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <div className="text-sm font-bold text-slate-700">Aucune affiche enregistrée pour ce panneau</div>
            <div className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
              Rendez-vous dans <b>Conception → Création des affiches</b> pour ajouter les affiches de ce panneau puis cliquez sur Enregistrer. La campagne sera ensuite visible ici.
            </div>
          </div>
        )}

        {campaign && !loading && (
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-slate-50 rounded-lg p-2.5 border">
              <div className="text-xs text-slate-600">
                <span className="font-bold">{campaign.panneau_type}</span>
                {campaign.media_type === 'video' ? ' · Vidéos' : ' · Images'}
                {campaign.updated_at && (
                  <span className="ml-2 text-slate-400">
                    · Mis à jour {new Date(campaign.updated_at).toLocaleString('fr-FR')}
                  </span>
                )}
              </div>
            </div>

            {/* Côté A */}
            <div>
              <div className="text-xs font-black uppercase tracking-wider text-blue-700 mb-2 flex items-center gap-2">
                <span className="w-6 h-6 bg-blue-600 text-white rounded flex items-center justify-center text-[10px]">A</span>
                {campaign.is_double_side ? 'Côté A — Recto' : 'Affiches 1 à 5'}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                {(campaign.cote_a_urls || Array(5).fill('')).map((url, i) =>
                  renderSlot(url, campaign.cote_a_names?.[i], campaign.cote_a_companies?.[i], i, 'A')
                )}
              </div>
            </div>

            {/* Côté B */}
            <div>
              <div className="text-xs font-black uppercase tracking-wider text-orange-700 mb-2 flex items-center gap-2">
                <span className="w-6 h-6 bg-orange-500 text-white rounded flex items-center justify-center text-[10px]">B</span>
                {campaign.is_double_side ? 'Côté B — Verso' : 'Affiches 6 à 10'}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                {(campaign.cote_b_urls || Array(5).fill('')).map((url, i) =>
                  renderSlot(url, campaign.cote_b_names?.[i], campaign.cote_b_companies?.[i], i + 5, 'B')
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
        </div>

        {/* Lightbox — image agrandie */}
        {lightbox && (
          <div
            className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-6 cursor-pointer"
            onClick={() => setLightbox(null)}
            data-testid="af-lightbox"
          >
            <button
              onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
              className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/40 rounded-full p-2"
              data-testid="af-lightbox-close"
            >
              <X className="h-6 w-6" />
            </button>
            <div className="max-w-5xl w-full flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-baseline gap-3 text-white">
                <span className="text-3xl font-black">#{lightbox.number}</span>
                {lightbox.company && (
                  <span className="text-lg font-bold uppercase tracking-wider">{lightbox.company}</span>
                )}
              </div>
              {lightbox.isVideo ? (
                <video
                  src={lightbox.url}
                  controls
                  autoPlay
                  className="max-h-[75vh] max-w-full rounded-lg shadow-2xl bg-black"
                />
              ) : (
                <img
                  src={lightbox.url}
                  alt={lightbox.name}
                  className="max-h-[75vh] max-w-full rounded-lg shadow-2xl object-contain bg-slate-800"
                />
              )}
              {lightbox.name && (
                <div className="text-xs text-white/70 font-mono">{lightbox.name}</div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
